import styled from 'styled-components';
import { Header } from '../../components/header';
import Footer from '../../components/footer';

const TermsContainer = styled.div`
  max-width: 1150px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  counter-reset: section;
  margin-top: 80px;
`;

const TermsHeader = styled.header`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 22px;
  margin-bottom: 10px;
  font-weight: bold;
`;

const VersionInfo = styled.div`
  font-size: 20px;
  margin-bottom: 10px;
  span {
    margin-right: 5px;
    font-weight: bold;
  }
  span:not(:first-child) {
    margin-left: 15px;
  }

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    
    span:not(:first-child) {
      margin-left: 0;
      margin-top: 5px;
    }
  }
`;

const TermsContent = styled.div`
  font-size: 20px;
`;

const Section = styled.section`
  margin-bottom: 10px;
  counter-increment: section;
  counter-reset: subsection;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 5px;

  &::before {
    content: counter(section) ". ";
  }
`;


const Subsection = styled.li`
  margin-bottom: 20px;
  counter-increment: subsection;
  position: relative;
  padding-left: 40px;
`;

const SubsectionTitle = styled.span`
  position: absolute;
  left: 0;
  
  &::before {
    content: counter(section) "." counter(subsection) ". ";
  }
`;

const SubsectionList = styled.ol`
  list-style-type: none;
  padding-left: 0;
  margin: 0;
`;

const SubsectionContent = styled.div`
  margin-left: 0;
`;

const EmailTemplate = styled.div`
  margin-left: 20px;
  line-height: 1.8;
`;

const TermsAndConditions = () => {
  return (
    <>
      <Header />
      <TermsContainer>
        <TermsHeader>
          <Title>Terms and Conditions for Vyga</Title>
          <VersionInfo>
            <span>Version:</span> v.1.4
          </VersionInfo>
          <VersionInfo>
            <span>Updated:</span> 2025 March 25
          </VersionInfo>
        </TermsHeader>

        <TermsContent>
          <p>
            By accessing or using Vyga (the "Platform"), you agree to be bound by these Terms and Conditions (the "Agreement" or "Terms") which also incorporates by reference the latest version of Vgga's privacy and security policy ("Privacy Policy"). Please read this Agreement and the Privacy Policy carefully before using the Platform. If you do not agree to both this Agreement's or Privacy Policy's terms, you must not use the Platform.
          </p>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Account Creation and Usage</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Eligibility:</u> You must be at least 14 years of age (or the legal age in your jurisdiction) to create an account or otherwise use the services provided by the Platform.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Account Information:</u> During registration, you agree to provide accurate, current, and complete information about yourself. You are responsible for ensuring that your account information remains up-to-date.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Account Termination and Rejection:</u> The Platform reserves the right to reject any application for an account or terminate an existing account at its sole discretion, including but not limited to cases where we believe the user has provided false information or engaged in fraudulent activity.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Account Security:</u> You are responsible for maintaining the confidentiality of your account credentials (username and password). You agree to notify us immediately if you suspect unauthorized access to your account.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Permitted Use:</u> The permitted use of this platform is to both a) collect market data and market research in the form of including but not limited to studies, rankings, indices and other surveys (collectively "Surveys") and b) distribute information from these Surveys or from other sources (collectively "Research Content"). These Surveys and Research Content may be proprietary or third-party related. The Surveys and Research Content on this Platform is for informational purposes only and should not be considered to be, nor utilized as, personal recommendations or advice.</SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Data Use</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>User Survey Data:</u> You hereby agree that any data that is generated by you voting on or otherwise stating your preferences in response to Platform-provided Surveys is not your personal data but rather is Platform-owned intellectual property based on users' reactions to the Platform. You also agree not to take screen shots of the Platform Surveys or Research Content, or otherwise copy or otherwise breach the intellectual property rights of the Platform or its licensors.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Platform User Experience Journey Data:</u> You hereby agree that any data the Platform collects regarding your user experience journey on the Platform (including without limitation the clicking of certain features as well as the inbound links from or outbound links to third-party websites or applications) is not your personal data but rather is Platform-owned intellectual property based on users' reactions to the Platform and its user journey. You also agree not to take screen shots of the Platform user journey or otherwise breach the copyrights of the Platform because you recognize that Platform's user experience is the sole intellectual property of the Platform.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Platform Data Use:</u> We will use the Survey information provided by you to the Platform, or collected from your use of the Platform to not only provide you and other users with insights, but to also enable the Platform's different monetization strategies including for example but not limited to a) targeted education and other communications delivered by the Platform to you and other users; b) generation of market research products such as indices and aggregated demographic profiles that are sold to third parties; and c) collection of deidentified data sold as AI/ML training data.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Intellectual Property Representation:</u> You will not reproduce, distribute, or resell any Research Content presented on, or Survey data collected by, the Platform without express written permission in the form of a mutually signed license agreement.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Industry Standards Representations:</u> The Platform will, where and to the degree applicable, do Surveys in accordance with accepted industry standards and practices, including but not limited to the ICC/ESOMAR International Code of Market, Opinion, and Social Research and Data Analytics. These standards ensure that all research activities are performed with the highest level of integrity, transparency, and ethical consideration. You as a user will similarly agree to be honest in your response to any Surveys or other end-user data submission requests, and otherwise answer in accordance to accepted industry standards and practices.</SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Cryptocurrency or other Financial Transactions</SectionTitle>
            <SubsectionList>
            <Subsection>
            <SubsectionTitle />
              <SubsectionContent>The Platform does not allow for cryptocurrency transactions or other financial transactions. Users are prohibited from soliciting, suggesting or facilitating such transactions on this platform.</SubsectionContent>
            </Subsection>
            </SubsectionList>
            
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Liability and Disclaimers</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>No Investment Advice:</u> The Platform does not provide financial, investment, or legal advice. All decisions to buy, sell, or trade cryptocurrencies on third-party platforms are made at your own discretion, and you should seek professional advice if needed.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>No Third-Party Product Advice:</u> The Platform does not provide consumer purchase advice. All decisions to purchase any third-party products or services based on information provided on the Platform are made at your own discretion.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Limitation of Liability:</u> To the fullest extent permitted by law, we eluc claim any liability for losses resulting from your use of the Platform, including but not limited to data</SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Termination of Service</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Platform's Right to Terminate:</u> We reserve the right to suspend or terminate your access to
                  the Platform at any time, with or without notice, for reasons including but not limited to
                  breach of this Agreement, illegal activity, or suspicious behavior.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>User's Right to Terminate:</u> You may terminate your account by following the instructions
                  provided on the Platform. Termination of your account does not relieve you of any
                  obligations incurred prior to account closure (including but not limited to your
                  recognition of the Platform`s intellectual property rights).</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Effect of Termination:</u> Upon termination of your account, all rights and licenses granted
                  to you under these Terms will immediately cease. You will no longer be able to access
                  your account or any funds associated with it unless otherwise required by law.  Your other
                  obligations in this Agreement, including recognition of the Platform`s intellectual
                  property rights, will continue to be enforceable by the Platform even after termination of
                  this Agreement.</SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Updates to Terms</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Right to Modify Terms:</u> The Platform reserves the right to update or modify these Terms
                  at any time. Changes will be posted on the Platform, and users will be notified of
                  significant changes.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>User Acknowledgment:</u> By continuing to use the Platform after the changes are made,
                  you accept and agree to the updated Terms. If you do not agree to the changes, you must
                  discontinue use of the Platform.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Version Control:</u> The date of the latest revision will be indicated at the top of these Terms,
                  and the most recent version will always be available for review on the Platform.</SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Governing Law</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <span>This Agreement, as well as any disputes or claims arising out of or in connection with
                  your use of the Platform, shall be governed by and construed in accordance with the laws
                  of Clark County, Washington state, USA, without regard to its conflict of law principles.
                  By accessing or using the Platform, you agree that any legal action or proceeding related
                  to these Terms and Conditions shall be exclusively brought in the courts located in
                  Vancouver, Clark County, Washington state, USA, and you consent to the personal
                  jurisdiction of such courts. If any provision of these Terms and Conditions is found to be
                  unlawful, void, or unenforceable, such provision will be deemed severable from these
                  Terms and Conditions and will not affect the validity and enforceability of the remaining
                  provisions.</span>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Contact Information</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent>
                  <u>Platform Contact Information:</u> For any questions or concerns regarding these Terms, or desire to notify the Platform of your intent to exercise certain GDPR or similar data rights, please contact us at:<br />
                  Vyga Inc.<br />
                  <a href="mailto:contact@vyga.co"><u>Admin@vyga.com</u></a>
                </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent>
                  <u>Your Communication Format:</u> You agree that the subject line of your email will follow 1 of the following 3 templates:
                  <EmailTemplate>
                    a) "Questions regarding Terms & Conditions"<br />
                    b) "Concerns regarding Terms & Conditions"<br />
                    c) "Desire to Exercise GDPR or Similar Data Rights"
                  </EmailTemplate>
                </SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>
        </TermsContent>
      </TermsContainer>
      <Footer />
    </>
  );
};

export default TermsAndConditions;